import { useState, useEffect } from "react";
import { WifiOff } from "lucide-react";

/**
 * Blocks the entire app whenever there's no real internet connection --
 * the deliberate fix for the one real gap in making tools installable as
 * shortcuts: usage metering and ad-serving both require a live network
 * round-trip (see useFreemium.ts, which explicitly "fails open" if its
 * server check can't complete). An installed shortcut that somehow still
 * functioned offline would mean unmetered, ad-free usage -- not a
 * technicality, a real business-model gap. Refusing to run at all
 * without a connection closes that gap structurally, rather than trying
 * to patch around every place a fails-open policy could be exploited.
 *
 * Reacts live to real connectivity changes (the `online`/`offline`
 * browser events), not just at initial page load -- if someone's
 * connection drops mid-session, this takes over immediately; if it
 * comes back, the app resumes automatically, no reload needed.
 *
 * One honest limitation: `navigator.onLine` reports whether a network
 * *interface* is active, not a mathematically guaranteed live path to
 * the internet (a wifi network with no real internet access is a known,
 * rare edge case it can't detect). Combined with the fact that
 * useFreemium's own real usage check still runs and would independently
 * fail without genuine connectivity, this is standard, well-established
 * practice for exactly this kind of gate -- not a theoretical gap left
 * unaddressed.
 */
export function OfflineGate({ children }: { children: React.ReactNode }) {
  const [isOnline, setIsOnline] = useState(() => (typeof navigator !== "undefined" ? navigator.onLine : true));

  useEffect(() => {
    const goOnline = () => setIsOnline(true);
    const goOffline = () => setIsOnline(false);
    window.addEventListener("online", goOnline);
    window.addEventListener("offline", goOffline);
    return () => {
      window.removeEventListener("online", goOnline);
      window.removeEventListener("offline", goOffline);
    };
  }, []);

  if (!isOnline) {
    return (
      <div className="min-h-screen flex items-center justify-center p-6 bg-background">
        <div className="text-center max-w-sm space-y-4">
          <div className="w-14 h-14 rounded-full bg-muted flex items-center justify-center mx-auto">
            <WifiOff className="h-6 w-6 text-muted-foreground" />
          </div>
          <h1 className="text-xl font-semibold">You're Offline</h1>
          <p className="text-sm text-muted-foreground leading-relaxed">
            FreeDailyPro needs an internet connection to work correctly. Please reconnect --
            this page will pick back up automatically once you're back online.
          </p>
        </div>
      </div>
    );
  }

  return <>{children}</>;
}
